export type FaseDeLlamada = 'idle' | 'requesting' | 'connecting' | 'ringing' | 'in_call' | 'ended' | 'failed';

export interface DestinoDeLlamada {
  contactId?: string;
  casoId?: string;
  label?: string;
}

export interface EstadoDeLlamada {
  fase: FaseDeLlamada;
  destino: DestinoDeLlamada | null;
  nombre: string;
  telefono: string;
  callId: string | null;
  silenciado: boolean;
  contestadaEn: number | null;
  terminadaEn: number | null;
  motivo: string | null;
}

export interface ResultadoDeLlamadaSip {
  destino: DestinoDeLlamada;
  fase: 'ended' | 'failed';
  contestada: boolean;
  segundosDeConversacion: number;
  motivo: string | null;
}

export type EventoDeLlamada =
  | { tipo: 'pedir'; destino: DestinoDeLlamada }
  | { tipo: 'credenciales'; callId: string; nombre: string; telefono: string }
  | { tipo: 'progreso' }
  | { tipo: 'contestada'; en: number }
  | { tipo: 'terminada'; en: number; motivo?: string | null }
  | { tipo: 'fallida'; en: number; motivo: string }
  | { tipo: 'colgar'; en: number }
  | { tipo: 'silencio'; valor: boolean }
  | { tipo: 'abortar' }
  | { tipo: 'descartar' };

export const ESTADO_INICIAL: EstadoDeLlamada = {
  fase: 'idle',
  destino: null,
  nombre: '',
  telefono: '',
  callId: null,
  silenciado: false,
  contestadaEn: null,
  terminadaEn: null,
  motivo: null,
};

const ACTIVAS: readonly FaseDeLlamada[] = ['requesting', 'connecting', 'ringing', 'in_call'];

export const estaOcupado = (estado: EstadoDeLlamada): boolean => ACTIVAS.includes(estado.fase);

export const MOTIVO_CANCELADA = 'Cancelaste la llamada.';

export const LLAMADA_EN_CURSO = 'Hay una llamada en curso.';

export function transicion(estado: EstadoDeLlamada, evento: EventoDeLlamada): EstadoDeLlamada {
  switch (evento.tipo) {
    case 'pedir':
      if (estaOcupado(estado)) return estado;
      return {
        ...ESTADO_INICIAL,
        fase: 'requesting',
        destino: evento.destino,
        nombre: evento.destino.label ?? '',
      };
    case 'credenciales':
      if (estado.fase !== 'requesting') return estado;
      return {
        ...estado,
        fase: 'connecting',
        callId: evento.callId,
        nombre: estado.nombre || evento.nombre,
        telefono: evento.telefono,
      };
    case 'progreso':
      return estado.fase === 'connecting' ? { ...estado, fase: 'ringing' } : estado;
    case 'contestada':
      if (estado.fase !== 'connecting' && estado.fase !== 'ringing') return estado;
      return { ...estado, fase: 'in_call', contestadaEn: evento.en };
    case 'terminada':
      if (!estaOcupado(estado)) return estado;
      return { ...estado, fase: 'ended', terminadaEn: evento.en, motivo: evento.motivo ?? null };
    case 'fallida':
      if (!estaOcupado(estado)) return estado;
      return { ...estado, fase: 'failed', terminadaEn: evento.en, motivo: evento.motivo };
    case 'colgar':
      if (!estaOcupado(estado)) return estado;
      return {
        ...estado,
        fase: 'ended',
        terminadaEn: evento.en,
        motivo: estado.fase === 'in_call' ? null : MOTIVO_CANCELADA,
      };
    case 'silencio':
      return estado.fase === 'in_call' ? { ...estado, silenciado: evento.valor } : estado;
    case 'abortar':
      return estado.fase === 'requesting' ? ESTADO_INICIAL : estado;
    case 'descartar':
      return estaOcupado(estado) ? estado : ESTADO_INICIAL;
  }
}

export function segundosDeConversacion(estado: EstadoDeLlamada, ahora: number): number {
  if (estado.contestadaEn === null) return 0;
  const hasta = estado.terminadaEn ?? ahora;
  return Math.max(0, Math.floor((hasta - estado.contestadaEn) / 1000));
}

export function resultadoDe(estado: EstadoDeLlamada): ResultadoDeLlamadaSip | null {
  if ((estado.fase !== 'ended' && estado.fase !== 'failed') || !estado.destino) return null;
  return {
    destino: estado.destino,
    fase: estado.fase,
    contestada: estado.contestadaEn !== null,
    segundosDeConversacion: segundosDeConversacion(estado, estado.terminadaEn ?? 0),
    motivo: estado.motivo,
  };
}

export const huboConversacion = (resultado: ResultadoDeLlamadaSip, minimoSegundos: number): boolean =>
  resultado.contestada && resultado.segundosDeConversacion >= minimoSegundos;

export interface Rechazo {
  fase: 'ended' | 'failed';
  motivo: string;
}

export function rechazoSip(codigo: number): Rechazo {
  if (codigo === 486 || codigo === 600) return { fase: 'ended', motivo: 'Ocupado.' };
  if (codigo === 408 || codigo === 480 || codigo === 487) return { fase: 'ended', motivo: 'No contestó.' };
  if (codigo === 603) return { fase: 'ended', motivo: 'Rechazó la llamada.' };
  if (codigo === 404 || codigo === 484 || codigo === 604) return { fase: 'failed', motivo: 'Número no disponible.' };
  if (codigo === 401 || codigo === 403 || codigo === 407) {
    return { fase: 'failed', motivo: 'El servidor de llamadas no autorizó la llamada.' };
  }
  return { fase: 'failed', motivo: 'No se pudo conectar la llamada.' };
}

export function motivoDeMicrofono(error: unknown): string {
  const nombre = typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : '';
  if (nombre === 'NotAllowedError' || nombre === 'SecurityError') {
    return 'El navegador no dio permiso para usar el micrófono. Actívalo en la barra de direcciones y vuelve a llamar.';
  }
  if (nombre === 'NotFoundError' || nombre === 'OverconstrainedError') {
    return 'No encontramos un micrófono. Conecta uno y vuelve a llamar.';
  }
  if (nombre === 'NotReadableError') {
    return 'Otra aplicación está usando el micrófono. Ciérrala y vuelve a llamar.';
  }
  return 'No pudimos abrir el micrófono.';
}

export function textoDeFase(estado: EstadoDeLlamada): string {
  switch (estado.fase) {
    case 'idle':
      return '';
    case 'requesting':
      return 'Preparando la llamada…';
    case 'connecting':
      return 'Conectando…';
    case 'ringing':
      return 'Llamando…';
    case 'in_call':
      return estado.silenciado ? 'En llamada · silenciado' : 'En llamada';
    case 'ended':
      return estado.motivo ?? 'Llamada terminada';
    case 'failed':
      return estado.motivo ?? 'No se pudo conectar la llamada.';
  }
}

export const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;

export const esTecla = (valor: string): boolean => (TECLAS as readonly string[]).includes(valor);
